import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-0 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-gradient-primary text-primary-foreground backdrop-blur-md shadow-glass hover:shadow-glass-hover border border-white/10",
        destructive: "bg-destructive/90 text-destructive-foreground backdrop-blur-md shadow-glass hover:shadow-glass-hover border border-white/10",
        outline: "border-2 border-primary/40 bg-background/40 backdrop-blur-md text-foreground shadow-glass hover:shadow-glass-hover hover:bg-gradient-primary hover:text-primary-foreground hover:border-transparent",
        secondary: "bg-gradient-secondary text-secondary-foreground backdrop-blur-md shadow-glass hover:shadow-glass-hover border border-white/10",
        ghost: "hover:bg-muted/50 backdrop-blur-sm hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        hero: "bg-gradient-primary text-primary-foreground backdrop-blur-md shadow-glass-hover hover:shadow-[var(--glow-primary)] font-semibold border border-white/20",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 rounded-lg px-3 text-xs",
        lg: "h-11 rounded-xl px-6",
        xl: "h-14 rounded-xl px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
